import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const REFERRAL_STORAGE_KEY = 'uplivion_referral_code';

export const useReferral = () => {
  const [searchParams] = useSearchParams();
  const [hasStoredReferral, setHasStoredReferral] = useState<boolean>(false);
  
  const referralToken = searchParams.get('token');
  const legacyReferralCode = searchParams.get('ref');
  const referralCode = referralToken || legacyReferralCode;

  // Store referral code in localStorage when detected
  useEffect(() => {
    if (referralCode) {
      localStorage.setItem(REFERRAL_STORAGE_KEY, referralCode);
      setHasStoredReferral(true);
      console.log('Referral code stored:', referralCode);
    } else {
      // Check if we have a stored referral code
      const stored = localStorage.getItem(REFERRAL_STORAGE_KEY);
      setHasStoredReferral(!!stored);
    }
  }, [referralCode]);

  const processReferralToken = async (userEmail: string) => {
    if (!referralToken) return null;
    
    try {
      const { data, error } = await supabase
        .from('referral_sessions')
        .select('referrer_profile_id')
        .eq('session_token', referralToken)
        .eq('is_used', false)
        .gt('expires_at', new Date().toISOString())
        .single();
      
      if (error || !data) {
        console.error('Error fetching referral session:', error);
        return null;
      }
      
      return data.referrer_profile_id;
    } catch (error) {
      console.error('Error processing referral token:', error);
      return null;
    }
  };

  const processStoredReferral = async (userId: string) => {
    const storedCode = localStorage.getItem(REFERRAL_STORAGE_KEY);
    console.log('🎯 Processing stored referral for user:', userId, 'with code:', storedCode);
    
    if (!storedCode) {
      console.log('❌ No stored referral code found');
      return null;
    }

    try {
      // Check if it's a token or legacy referral code
      if (storedCode.length > 16) {
        console.log('🔑 Processing session token:', storedCode.substring(0, 8) + '...');
        
        // Likely a session token - process with user ID
        const { data, error } = await supabase
          .from('referral_sessions')
          .select('referrer_profile_id')
          .eq('session_token', storedCode)
          .eq('is_used', false)
          .gt('expires_at', new Date().toISOString())
          .single();
        
        console.log('📋 Session lookup result:', { data, error });
        
        if (!error && data) {
          console.log('✅ Valid session found, processing referral...');
          
          // Mark session as used and create referral
          const { data: processResult, error: processError } = await supabase.rpc('process_referral_session', {
            session_token_param: storedCode,
            user_id_param: userId
          });
          
          console.log('🔄 Referral processing result:', { processResult, processError });
          
          if (!processError && processResult && typeof processResult === 'object' && 'success' in processResult && processResult.success) {
            localStorage.removeItem(REFERRAL_STORAGE_KEY);
            console.log('🎉 Referral processed successfully! Referred by:', data.referrer_profile_id);
            return data.referrer_profile_id;
          } else {
            console.error('❌ Failed to process referral session:', processError || processResult);
          }
        }
      } else {
        console.log('🔖 Processing legacy referral code:', storedCode);
        
        // Legacy referral code - process directly
        const { data, error } = await supabase.rpc('process_referral_session', {
          session_token_param: storedCode,
          user_id_param: userId
        });

        console.log('📋 Legacy referral processing result:', { data, error });

        if (!error && data && typeof data === 'object' && 'success' in data && data.success) {
          localStorage.removeItem(REFERRAL_STORAGE_KEY);
          console.log('🎉 Legacy referral processed successfully!');
          return 'referrer_profile_id' in data ? data.referrer_profile_id as string : null;
        } else {
          console.error('❌ Failed to process legacy referral:', error || data);
        }
      }
    } catch (error) {
      console.error('💥 Exception during referral processing:', error);
    }

    console.log('⚠️ Referral processing failed or no valid referral found');
    return null;
  };

  const clearStoredReferral = () => {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
    setHasStoredReferral(false);
  };

  return { 
    referralCode, 
    hasStoredReferral,
    processReferralToken, 
    processStoredReferral,
    clearStoredReferral
  };
};